import { Component, OnInit } from '@angular/core';
import { Router, Routes, NavigationEnd } from '@angular/router';
import { AuthService } from '../shared/auth-service/auth.service';

interface RouteInfo {
    path: string;
    title: string;
    description: string;
    icon: string;
    requiresAuth: boolean;
}

@Component({
    selector: 'app-landing',
    templateUrl: './landing.component.html',
    styleUrls: ['./landing.component.scss'],
    standalone: false,
})
export class LandingComponent implements OnInit {
    public loggedIn!: boolean;
    routes: RouteInfo[] = [];

    constructor(
        private router: Router,
        private authService: AuthService
    ) { }

    ngOnInit(): void {
        this.loggedIn = this.isLoggedIn();
        this.extractRoutes();
    }

    private isLoggedIn(): boolean {
        return !!this.authService.getToken();
    }

    public logout(): void {
        this.authService.logout();
        this.loggedIn = false;
    }

    private extractRoutes(): void {
        const routes = this.router.config;
        this.routes = routes
            .filter(route => route.path && route.path !== '**' && route.path !== 'login')
            .map(route => this.mapRouteToInfo(route))
            .filter(routeInfo => routeInfo !== null) as RouteInfo[];
    }

    private mapRouteToInfo(route: any): RouteInfo | null {
        const routeMap: { [key: string]: { title: string; description: string; icon: string; requiresAuth: boolean } } = {
            'articles': {
                title: 'Article Staging',
                description: 'Import and review new articles and their DXF files for inclusion in BPSolver.',
                icon: 'bi-upload',
                requiresAuth: true
            },
            'articles-management': {
                title: 'Articles Management',
                description: 'Manage and configure article definitions and their properties.',
                icon: 'bi-gear',
                requiresAuth: true
            },
            'systems': {
                title: 'Systems Management',
                description: 'Manage and configure system definitions for BPSolver.',
                icon: 'bi-diagram-3',
                requiresAuth: true
            },
            'rules-dashboard': {
                title: 'Rules Dashboard',
                description: 'Overview and analytics for all rule types in the system.',
                icon: 'bi-graph-up',
                requiresAuth: true
            },
            'article-viewer': {
                title: 'Article Viewer',
                description: 'View and interact with 3D article models.',
                icon: 'bi-eye',
                requiresAuth: true
            },
            'viewer': {
                title: 'Model Viewer',
                description: 'Advanced 3D model viewing capabilities.',
                icon: 'bi-cube',
                requiresAuth: true
            },
            'dxf-to-jpg': {
                title: 'DXF to JPG Converter',
                description: 'Convert DXF files to JPG images.',
                icon: 'bi-image',
                requiresAuth: true
            },
            'decision-tree': {
                title: 'Decision',
                description: 'Navigate and manage decision tree logic.',
                icon: 'bi-diagram-2',
                requiresAuth: true
            }
        };

        const routeInfo = routeMap[route.path];
        if (!routeInfo) {
            return null;
        }

        return {
            path: route.path,
            title: routeInfo.title,
            description: routeInfo.description,
            icon: routeInfo.icon,
            requiresAuth: routeInfo.requiresAuth
        };
    }

    navigateToRoute(path: string): void {
        this.router.navigate([`/${path}`]);
    }

    getAvailableRoutes(): RouteInfo[] {
        if (this.loggedIn) {
            return this.routes;
        } else {
            return this.routes.filter(route => !route.requiresAuth);
        }
    }

    login(): void {
        this.router.navigate(['/login']);
    }

    hasProtectedRoutes(): boolean {
        return this.routes.some(route => route.requiresAuth);
    }

    getProtectedRoutes(): RouteInfo[] {
        return this.routes.filter(route => route.requiresAuth);
    }
}
